import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDatabase, ref, get } from 'firebase/database';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';
import { Container, Card, Button, Form, Alert, Modal, Badge, Table, Dropdown, Spinner } from 'react-bootstrap';
import { Plus, Calendar, Settings, Clock, AlertTriangle, CheckCircle, Check, BarChart3, MoreVertical, Trash2, FileText, Camera, Smartphone } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { getAuth } from 'firebase/auth';
import { useBuilding } from '../../Helpers/BuildingContext';
import { useBuildingsData } from '../../Helpers/BuildingSummariesContext';
import { useUsersData } from '../../Helpers/UsersContext';
import {
    BuildingCheckWithTemplate,
    CheckCompletion,
    formatDate,
    lastCompletedDate,
    formatDaysUntilDue,
    getDaysUntilDue,
    calculateNextDueDate,
    getEffectiveValues,
    loadAllBuildingChecks,
    checkDisplayName
} from './buildingChecksUtils';
import { InspectionCategory, TEMPLATE_CATEGORIES } from './types';
import { translateInspectionCategory, translateRegulatoryType } from '../../types/EnumTranslations';
import { buildingChecksApi } from '../../api/buildingChecksApi';
import './BuildingChecksPage.css';
import CheckLocationSection from './components/CheckLocationSection';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { formatDateOnlyForStorage } from '../../Helpers/DateUtils';
import { usePhotoUpload } from '../../hooks/usePhotoUpload';
import { useCheckComponentNames } from '../../hooks/inspections/useCheckComponentNames';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';

const AllBuildingChecksPage: React.FC = () => {
    const auth = getAuth();
    const user = auth.currentUser;
    const { buildings } = useBuildingsData();
    const { users } = useUsersData();
    // Removing a check is admin-only (buildingChecksApi.removeBuildingChecks)
    const isAdmin = users?.find(u => u.key === user?.uid)?.admin || false;
    const [allChecks, setAllChecks] = useState<BuildingCheckWithTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);

    // Completion modal state
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [checkToComplete, setCheckToComplete] = useState<BuildingCheckWithTemplate | null>(null);
    const [saving, setSaving] = useState(false);
    const [modalAlert, setModalAlert] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);
    const [completionForm, setCompletionForm] = useState({
        completedAt: '',
        measurementValue: '',
        notes: ''
    });

    // Photo upload hook for completion modal
    const {
        photos: completionPhotos,
        isDragOver,
        isUploading: isUploadingPhotos,
        uploadProgress,
        fileInputRef: completionFileInputRef,
        addPhotos: addCompletionPhotos,
        removePhoto: removeCompletionPhoto,
        clearPhotos: clearCompletionPhotos,
        uploadPhotos,
        handleFileSelect: handleCompletionFileSelect,
        dragHandlers: completionDragHandlers
    } = usePhotoUpload({
        onError: (message) => setModalAlert({ type: 'danger', message })
    });

    // Statistics modal state
    const [showStatsModal, setShowStatsModal] = useState(false);
    const [checkForStats, setCheckForStats] = useState<BuildingCheckWithTemplate | null>(null);
    const [checkResults, setCheckResults] = useState<CheckCompletion[]>([]);
    
    // Delete confirmation modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [checkToDelete, setCheckToDelete] = useState<BuildingCheckWithTemplate | null>(null);

    // Category filter state
    const [categoryFilter, setCategoryFilter] = useState<string>('');

    const db = getDatabase();
    // Checks of every building: component names are read per check
    const componentNameOf = useCheckComponentNames(allChecks);
    const checkName = (check: BuildingCheckWithTemplate) =>
        checkDisplayName(check, check.template.name, { componentName: key => componentNameOf(check.buildingId, key) });

    // Set document title
    useDocumentTitle('Alla kommande aktiviteter');

    // Load all building checks
    useEffect(() => {
        if (buildings && buildings.length > 0) {
            loadAllChecks();
        }
    }, [buildings]);

    const loadAllChecks = async () => {
        if (!buildings || buildings.length === 0) {
            setAllChecks([]);
            setLoading(false);
            return;
        }
        
        try {
            setLoading(true);
            const buildingsForChecks = buildings.map(building => ({
                key: building.key,
                name: building.name || 'Namnlös byggnad'
            }));
            const checks = await loadAllBuildingChecks(buildingsForChecks);
            setAllChecks(checks);
        } catch (error) {
            console.error('Error loading all checks:', error);
            setAlert({ type: 'danger', message: 'Fel vid laddning av aktiviteter' });
        } finally {
            setLoading(false);
        }
    };


    const openCompletionModal = (check: BuildingCheckWithTemplate) => {
        setCheckToComplete(check);
        setCompletionForm({
            completedAt: formatDateOnlyForStorage(new Date()),
            measurementValue: '',
            notes: ''
        });
        setModalAlert(null); // Clear any previous modal alerts
        // Reset photo state
        clearCompletionPhotos();
        setShowCompletionModal(true);
    };

    const openStatsModal = async (check: BuildingCheckWithTemplate) => {
        if (!check.buildingId) return;
        
        setCheckForStats(check);
        setShowStatsModal(true);
        await loadCheckResults(check.buildingId, check.checkKey);
    };

    const loadCheckResults = async (buildingId: string, checkKey: string) => {
        try {
            const resultsRef = ref(db, `inspections/completions/${buildingId}/${checkKey}`);
            const resultsSnapshot = await get(resultsRef);
            
            if (resultsSnapshot.exists()) {
                const resultsData = resultsSnapshot.val();
                const results: CheckCompletion[] = [];
                
                for (const [date, completionData] of Object.entries(resultsData)) {
                    results.push({
                        ...(completionData as CheckCompletion),
                        completedAt: date
                    });
                }
                
                results.sort((a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime());
                setCheckResults(results);
            } else {
                setCheckResults([]);
            }
        } catch (error) {
            console.error('Error loading check results:', error);
            setCheckResults([]);
        }
    };

    const handleCompleteCheck = async () => {
        if (!checkToComplete || !user || !checkToComplete.buildingId) {
            setAlert({ type: 'danger', message: 'Fel: Saknar nödvändig information' });
            return;
        }

        // Validate future date - completion date cannot be in the future
        const today = formatDateOnlyForStorage(new Date());
        if (completionForm.completedAt > today) {
            setModalAlert({ type: 'danger', message: 'Slutförandedatum kan inte vara i framtiden.' });
            return;
        }

        // Validate measurement if required
        if (checkToComplete.template.takesMeasurement && (!completionForm.measurementValue || completionForm.measurementValue.trim() === '')) {
            setModalAlert({ type: 'danger', message: 'Mätvärde krävs för denna aktivitet. Vänligen ange ett värde.' });
            return;
        }

        try {
            setSaving(true);

            // One completion per check and day
            if (await buildingChecksApi.hasCompletionOn(checkToComplete.buildingId, checkToComplete.checkKey, completionForm.completedAt)) {
                setModalAlert({ type: 'danger', message: 'En kontroll har redan registrerats för detta datum. Välj ett annat datum.' });
                setSaving(false);
                return;
            }

            // Photos first, under the completion (the only part of it clients write);
            // completeInspection then writes the record and moves the due date.
            if (completionPhotos.length > 0) {
                const basePath = `inspections/completions/${checkToComplete.buildingId}/${checkToComplete.checkKey}/${completionForm.completedAt}/photos`;
                await uploadPhotos({
                    storagePath: (photoKey) => `${basePath}/${photoKey}`,
                    dbPath: (photoKey) => `${basePath}/${photoKey}`
                });
            }

            await buildingChecksApi.completeBuildingCheck({
                buildingKey: checkToComplete.buildingId,
                checkKey: checkToComplete.checkKey,
                completedDate: completionForm.completedAt,
                summaryComment: completionForm.notes.trim(),
                checkLabel: checkName(checkToComplete),
                ...(checkToComplete.template.takesMeasurement && completionForm.measurementValue && {
                    measurementValue: parseFloat(completionForm.measurementValue)
                })
            });

            setAlert({ type: 'success', message: 'Kontroll markerad som klar!' });
            setShowCompletionModal(false);
            setCheckToComplete(null);
            setCompletionForm({ completedAt: '', measurementValue: '', notes: '' });
            loadAllChecks();
        } catch (error) {
            console.error('Error completing check:', error);
            setAlert({ type: 'danger', message: 'Fel vid markering av aktivitet som klar' });
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteCheck = async () => {
        if (!checkToDelete || !checkToDelete.buildingId) {
            setAlert({ type: 'danger', message: 'Fel: Saknar nödvändig information' });
            return;
        }

        try {
            setSaving(true);
            
            await buildingChecksApi.removeBuildingCheck(checkToDelete.buildingId, checkToDelete.checkKey);

            setAlert({ type: 'success', message: 'Kontroll borttagen framgångsrikt!' });
            setShowDeleteModal(false);
            setCheckToDelete(null);
            loadAllChecks();
        } catch (error) {
            console.error('Error deleting check:', error);
            setAlert({ type: 'danger', message: 'Fel vid borttagning av aktivitet' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <>
            <BreadcrumbComponent items={[{ text: 'Alla kommande aktiviteter' }]} />
            <Container className="mt-4">
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Laddar...</span>
                    </div>
                    <p className="mt-2">Laddar aktiviteter...</p>
                </div>
            </Container>
            </>
        );
    }

    return (
        <>
        <BreadcrumbComponent items={[{ text: 'Alla kommande aktiviteter' }]} />
        <Container className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1 className="page-title">Alla kommande aktiviteter</h1>
                <div className="d-flex gap-2 align-items-center">
                    <Form.Select
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        className="w-auto"
                        style={{ minWidth: '160px' }}
                    >
                        <option value="">Alla kategorier</option>
                        {TEMPLATE_CATEGORIES.map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                    </Form.Select>
                    <Link to="/settings/building-checks" className="btn btn-outline-secondary">
                        <Settings size={16} className="me-1" />
                        Hantera mallar
                    </Link>
                </div>
            </div>

            {alert && (
                <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>
                    {alert.message}
                </Alert>
            )}

            {allChecks.length === 0 ? (
                <Card>
                    <Card.Body className="text-center py-5">
                        <Clock size={48} className="text-muted mb-3" />
                        <h5>Inga aktiviteter hittades</h5>
                        <p className="text-muted">
                            Det finns inga aktiva aktiviteter i någon byggnad.
                        </p>
                    </Card.Body>
                </Card>
            ) : (
                <div className="std-box dropshadow" style={{ borderRadius: '0.5rem', padding: '1rem', marginBottom: '50px' }}>
                        <Table responsive striped hover>
                            <thead>
                                <tr>
                                    <th>Byggnad</th>
                                    <th>Kontroll</th>
                                    <th>Förfallodatum</th>
                                    <th>Senast genomförd</th>
                                    <th>Åtgärder</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allChecks
                                    .filter((check) => {
                                        if (!categoryFilter) return true;
                                        const category = check.template?.category || InspectionCategory.MeterReadings;
                                        return category === categoryFilter;
                                    })
                                    .map((check) => {
                                    const daysText = formatDaysUntilDue(check.nextDueDate);
                                    const today = formatDateOnlyForStorage(new Date());
                                    const isLastToday = lastCompletedDate(check) === today;
                                    
                                    return (
                                        <tr key={`${check.buildingId}-${check.checkKey}`}>
                                            <td>
                                                <strong>{check.buildingName}</strong>
                                            </td>
                                            <td>
                                                <strong>{checkName(check)}</strong>
                                                {check.template.category && (
                                                    <Badge
                                                        bg="secondary"
                                                        className="ms-2"
                                                    >
                                                        {translateInspectionCategory(check.template.category)}
                                                    </Badge>
                                                )}
                                                {check.template.regulatoryType && (
                                                    <Badge
                                                        bg="warning"
                                                        text="dark"
                                                        className="ms-2"
                                                    >
                                                        {translateRegulatoryType(check.template.regulatoryType)}
                                                    </Badge>
                                                )}
                                                {check.template.estimatedTime > 0 && (
                                                    <div className="small text-muted">
                                                        {check.template.estimatedTime} min
                                                    </div>
                                                )}
                                                {check.template.takesMeasurement && (
                                                    <div className="small text-muted mt-1">
                                                        <strong>Mätvärde:</strong> {check.template.takesMeasurement.metric}
                                                        {check.template.takesMeasurement.acceptableRange && (
                                                            <span>
                                                                {check.template.takesMeasurement.acceptableRange.min !== undefined && check.template.takesMeasurement.acceptableRange.max !== undefined && (
                                                                    <span> ({check.template.takesMeasurement.acceptableRange.min} - {check.template.takesMeasurement.acceptableRange.max})</span>
                                                                )}
                                                                {check.template.takesMeasurement.acceptableRange.min !== undefined && check.template.takesMeasurement.acceptableRange.max === undefined && (
                                                                    <span> (min: {check.template.takesMeasurement.acceptableRange.min})</span>
                                                                )}
                                                                {check.template.takesMeasurement.acceptableRange.min === undefined && check.template.takesMeasurement.acceptableRange.max !== undefined && (
                                                                    <span> (max: {check.template.takesMeasurement.acceptableRange.max})</span>
                                                                )}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <div>{formatDate(check.nextDueDate)}</div>
                                                <div className={`small ${
                                                    getDaysUntilDue(check.nextDueDate) < 0 ? 'text-danger' : 
                                                    getDaysUntilDue(check.nextDueDate) === 0 ? 'text-warning' : 
                                                    getDaysUntilDue(check.nextDueDate) <= check.template.advanceNotice ? 'text-danger' : 
                                                    'text-muted'
                                                }`}>
                                                    {daysText}
                                                </div>
                                            </td>
                                            <td>
                                                {lastCompletedDate(check) ? (
                                                    <div>
                                                        <div className="small d-flex align-items-center">
                                                            {formatDate(lastCompletedDate(check)!)}
                                                            {isLastToday && (
                                                                <Check size={16} className="text-success ms-1" />
                                                            )}
                                                        </div>
                                                        {check.template.takesMeasurement && check.lastMeasurement && (
                                                            <div className="small text-muted">
                                                                {check.lastMeasurement.value} {check.lastMeasurement.unit}
                                                                {!check.lastMeasurement.withinRange && (
                                                                    <span className="text-danger ms-1">⚠</span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-muted small">Aldrig genomförd</span>
                                                )}
                                            </td>
                                            <td>
                                                <Dropdown>
                                                    <Dropdown.Toggle variant="outline-secondary" size="sm" id={`dropdown-${check.buildingId}-${check.checkKey}`}>
                                                        <MoreVertical size={16} />
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu>
                                                        {check.template.category === InspectionCategory.ConditionAssessment ? (
                                                            // Skickbedömning is completed in the iOS app only (D15)
                                                            <Dropdown.Item disabled title="Skickbedömningen markeras som utförd i appen">
                                                                <Smartphone size={16} className="me-2" />
                                                                Utförs i appen
                                                            </Dropdown.Item>
                                                        ) : (
                                                            <Dropdown.Item onClick={() => openCompletionModal(check)}>
                                                                <CheckCircle size={16} className="me-2" />
                                                                Markera som utförd
                                                            </Dropdown.Item>
                                                        )}
                                                        <Dropdown.Item onClick={() => openStatsModal(check)}>
                                                            <BarChart3 size={16} className="me-2" />
                                                            Visa statistik
                                                        </Dropdown.Item>
                                                        {isAdmin && <Dropdown.Divider />}
                                                        {isAdmin && (
                                                            <Dropdown.Item
                                                                onClick={() => {
                                                                    setCheckToDelete(check);
                                                                    setShowDeleteModal(true);
                                                                }}
                                                                className="text-danger"
                                                            >
                                                                <Trash2 size={16} className="me-2" />
                                                                Radera
                                                            </Dropdown.Item>
                                                        )}
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                        {categoryFilter && allChecks.filter(check => {
                            const category = check.template?.category || InspectionCategory.MeterReadings;
                            return category === categoryFilter;
                        }).length === 0 && (
                            <p className="text-muted text-center mt-3">Inga aktiviteter i denna kategori</p>
                        )}
                </div>
            )}

            {/* Completion Modal */}
            <Modal show={showCompletionModal} onHide={() => setShowCompletionModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Markera aktivitet som klar</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalAlert && (
                        <Alert variant={modalAlert.type} className="mb-3">
                            {modalAlert.message}
                        </Alert>
                    )}
                    {checkToComplete && (
                        <>
                            <div className="mb-3">
                                <h6>Kontrollinformation</h6>
                                <p><strong>Byggnad:</strong> {checkToComplete.buildingName}</p>
                                <p><strong>Namn:</strong> {checkName(checkToComplete)}</p>
                                <p style={{ whiteSpace: 'pre-wrap' }}><strong>Beskrivning:</strong> {getEffectiveValues(checkToComplete, checkToComplete.template).description}</p>
                                <p><strong>Förfallodatum:</strong> {formatDate(checkToComplete.nextDueDate)}</p>
                            </div>

                            <CheckLocationSection buildingKey={checkToComplete.buildingId} check={checkToComplete} />

                            {/* Instructional Attachments (merged: template + building-specific) */}
                            {(() => {
                                const mergedAttachments = getEffectiveValues(checkToComplete, checkToComplete.template).attachments;
                                return mergedAttachments && Object.keys(mergedAttachments).length > 0 && (
                                    <div className="mb-3">
                                        <h6>Instruktionsbilagor</h6>
                                        <div className="d-flex flex-wrap gap-2">
                                            {Object.entries(mergedAttachments).map(([dbKey, attachment]) => (
                                                <Button
                                                    key={dbKey}
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="d-flex align-items-center"
                                                    onClick={() => {
                                                        const storage = getStorage();
                                                        const fileRef = storageRef(storage, attachment.storagePath);
                                                        getDownloadURL(fileRef).then((url) => {
                                                            window.open(url, '_blank');
                                                        }).catch((error) => {
                                                            console.error('Error getting download URL:', error);
                                                            window.alert('Fel vid hämtning av fil');
                                                        });
                                                    }}
                                                >
                                                    <FileText size={14} className="me-1" />
                                                    {attachment.key}
                                                </Button>
                                            ))}
                                        </div>
                                        <small className="text-muted">Klicka på en bilaga för att öppna den i en ny flik</small>
                                    </div>
                                );
                            })()}

                            <Form.Group className="mb-3">
                                <Form.Label>När genomfördes aktiviteten? *</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={completionForm.completedAt}
                                    onChange={(e) => setCompletionForm(prev => ({ ...prev, completedAt: e.target.value }))}
                                    required
                                />
                                {completionForm.completedAt && checkToComplete && (
                                    <div className="mt-2">
                                        <small className="text-muted">
                                            Nästa förfallodatum beräknat på valt datum: {calculateNextDueDate(
                                                checkToComplete.template.nextDueDateCalculation,
                                                getEffectiveValues(checkToComplete, checkToComplete.template).interval,
                                                completionForm.completedAt,
                                                checkToComplete.nextDueDate
                                            )}
                                        </small>
                                    </div>
                                )}
                            </Form.Group>

                            {checkToComplete.template.takesMeasurement && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Mätvärde ({checkToComplete.template.takesMeasurement.metric}) *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="any"
                                        value={completionForm.measurementValue}
                                        onChange={(e) => setCompletionForm(prev => ({ ...prev, measurementValue: e.target.value }))}
                                        placeholder="Ange mätvärde"
                                        required
                                    />
                                    {checkToComplete.template.takesMeasurement.acceptableRange && (
                                        <Form.Text className="text-muted">
                                            Acceptabelt intervall: 
                                            {checkToComplete.template.takesMeasurement.acceptableRange.min !== undefined && 
                                             checkToComplete.template.takesMeasurement.acceptableRange.max !== undefined && (
                                                <span> {checkToComplete.template.takesMeasurement.acceptableRange.min} - {checkToComplete.template.takesMeasurement.acceptableRange.max}</span>
                                            )}
                                            {checkToComplete.template.takesMeasurement.acceptableRange.min !== undefined && 
                                             checkToComplete.template.takesMeasurement.acceptableRange.max === undefined && (
                                                <span> Min: {checkToComplete.template.takesMeasurement.acceptableRange.min}</span>
                                            )}
                                            {checkToComplete.template.takesMeasurement.acceptableRange.min === undefined && 
                                             checkToComplete.template.takesMeasurement.acceptableRange.max !== undefined && (
                                                <span> Max: {checkToComplete.template.takesMeasurement.acceptableRange.max}</span>
                                            )}
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label>Anteckningar</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={completionForm.notes}
                                    onChange={(e) => setCompletionForm(prev => ({ ...prev, notes: e.target.value }))}
                                    placeholder="Valfria anteckningar om genomförandet..."
                                />
                            </Form.Group>

                            {/* Photo Upload */}
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    <Camera size={16} className="me-1" />
                                    Bilder (valfritt)
                                </Form.Label>
                                <div
                                    className={`completion-dropzone${isDragOver ? ' completion-dropzone--drag-over' : ''}${saving || isUploadingPhotos ? ' completion-dropzone--disabled' : ''}`}
                                    {...completionDragHandlers}
                                    onClick={() => !(saving || isUploadingPhotos) && completionFileInputRef.current?.click()}
                                >
                                    <input
                                        ref={completionFileInputRef}
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={handleCompletionFileSelect}
                                        style={{ display: 'none' }}
                                        disabled={saving || isUploadingPhotos}
                                    />
                                    <div className="completion-dropzone__text">
                                        {isDragOver ? (
                                            <span>Släpp bilder här</span>
                                        ) : (
                                            <span>
                                                Dra och släpp bilder här, eller klicka för att välja
                                                <br />
                                                <small>Max 10 bilder, 10 MB per bild</small>
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Photo Previews */}
                                {completionPhotos.length > 0 && (
                                    <div className="completion-photos">
                                        {completionPhotos.map(photo => (
                                            <div key={photo.id} className="completion-photo">
                                                <img
                                                    src={photo.previewUrl}
                                                    alt="Preview"
                                                    className="completion-photo__image"
                                                />
                                                <button
                                                    type="button"
                                                    className="completion-photo__remove"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        removeCompletionPhoto(photo.id);
                                                    }}
                                                    disabled={saving || isUploadingPhotos}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCompletionModal(false)} disabled={saving || isUploadingPhotos}>
                        Avbryt
                    </Button>
                    <Button
                        variant="success"
                        onClick={handleCompleteCheck}
                        disabled={!completionForm.completedAt || saving || isUploadingPhotos}
                    >
                        {saving || isUploadingPhotos ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                {uploadProgress
                                    ? `Laddar upp bild ${uploadProgress.current}/${uploadProgress.total}...`
                                    : 'Sparar...'
                                }
                            </>
                        ) : (
                            'Markera som klar'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Statistics Modal */}
            <Modal show={showStatsModal} onHide={() => setShowStatsModal(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <BarChart3 size={20} className="me-2" />
                        Statistik och loggar - {checkForStats && checkName(checkForStats)}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {checkForStats && (
                        <>
                            {/* Where the check is: its component, or its place */}
                            <CheckLocationSection buildingKey={checkForStats.buildingId} check={checkForStats} />

                            {checkResults.length === 0 ? (
                                <div className="text-center py-4">
                                    <BarChart3 size={48} className="text-muted mb-3" />
                                    <h5>Inga resultat ännu</h5>
                                    <p className="text-muted">
                                        Denna aktivitet har inte genomförts ännu. Resultat och statistik kommer att visas här efter första genomförandet.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    {/* Chart for measurements */}
                                    {checkForStats.template.takesMeasurement && checkResults.some(r => r.measurement) && (
                                        <div className="mb-4">
                                            <h6>Mätvärden över tid</h6>
                                            <div style={{ height: '300px' }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart
                                                        data={checkResults
                                                            .filter(r => r.measurement)
                                                            .map(r => ({
                                                                date: new Date(r.completedAt).toLocaleDateString('sv-SE'),
                                                                value: r.measurement?.value || 0,
                                                                withinRange: r.measurement?.withinRange || false
                                                            }))
                                                            .reverse()
                                                        }
                                                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis 
                                                            dataKey="date" 
                                                            angle={-45}
                                                            textAnchor="end"
                                                            height={80}
                                                        />
                                                        <YAxis 
                                                            label={{ value: checkForStats.template.takesMeasurement?.metric || '', angle: -90, position: 'insideLeft' }}
                                                        />
                                                        <Tooltip 
                                                            formatter={(value: any, name: string) => [
                                                                `${value} ${checkForStats.template.takesMeasurement?.metric || ''}`,
                                                                'Värde'
                                                            ]}
                                                            labelFormatter={(label) => `Datum: ${label}`}
                                                        />
                                                        <Bar 
                                                            dataKey="value" 
                                                            fill="#8884d8"
                                                            name="Mätvärde"
                                                        />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    )}

                                    {/* Results table */}
                                    <div>
                                        <h6>Alla genomföranden ({checkResults.length})</h6>
                                        <Table responsive striped size="sm">
                                            <thead>
                                                <tr>
                                                    <th>Datum</th>
                                                    <th>Genomförd av</th>
                                                    {checkForStats.template.takesMeasurement && <th>Mätvärde</th>}
                                                    <th>Anteckningar</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {checkResults.map((result, index) => (
                                                    <tr key={index}>
                                                        <td>{formatDate(result.completedAt)}</td>
                                                        <td>
                                                            <small className="text-muted">
                                                                {new Date(result.submittedAt).toLocaleDateString('sv-SE')} {new Date(result.submittedAt).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                                                            </small>
                                                        </td>
                                                        {checkForStats.template.takesMeasurement && (
                                                            <td>
                                                                {result.measurement ? (
                                                                    <div>
                                                                        <span className={result.measurement.withinRange ? 'text-success' : 'text-danger'}>
                                                                            {result.measurement.value} {result.measurement.unit}
                                                                        </span>
                                                                        {!result.measurement.withinRange && (
                                                                            <Badge bg="danger" className="ms-1 small">Utanför gränsvärde</Badge>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-muted">-</span>
                                                                )}
                                                            </td>
                                                        )}
                                                        <td>
                                                            {result.summaryComment ? (
                                                                <small>{result.summaryComment}</small>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowStatsModal(false)}>
                        Stäng
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Bekräfta radering</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {checkToDelete && (
                        <>
                            <p>Är du säker på att du vill radera aktiviteten <strong>"{checkName(checkToDelete)}"</strong> från <strong>"{checkToDelete.buildingName}"</strong>?</p>
                            <Alert variant="info" className="mt-3">
                                <strong>Obs:</strong> Eventuell historik kommer att finnas kvar även efter radering.
                            </Alert>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Avbryt
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleDeleteCheck}
                        disabled={saving}
                    >
                        {saving ? 'Raderar...' : 'Radera'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
        </>
    );
};

export default AllBuildingChecksPage;
